import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Flex, Row, Col, Typography, Spin } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { PAIC_TAB_KEYS } from "constants/peoplePlusAI.constants";
import GroupPageHeader from "Pages/SkillsBridgerV2/HrSkillsBridger/shared/GroupPageHeader";
import SkillsChart from "./components/SkillsChart";
import SbiCard from "./components/SbiCard";
import EmployeeProfileCard from "./components/EmployeeProfileCard";
import {
  SkillsMapContainerStyled,
  ContentAreaStyled,
  ComingSoonStyled,
} from "./SbSkillsMapV2.styles";
import { TEXT, ROUTE } from "./SbSkillsMapV2.constants";
import { COMING_SOON_TABS } from "./GroupSkillsMapV2.constants";
import {
  PAGE_TEXT,
  SKILLS_BRIDGER_DROPDOWN_ITEMS,
  buildEmployeeHeaderSubtitle,
  transformSkillsForChart,
} from "./EmployeeSkillsMapV2.constants";
import { getSBEmployeeJdSkillAnalysis, getEmployeeSummary } from "Api/apiService";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Text } = Typography;

const ANALYTICS_FROM = "skills_bridger_employee_skills_map";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.SKILLS_BRIDGER.SKILLS_MAP_VIEWED;

const EmployeeSkillsMapV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const navigate     = useNavigate();
  const { state }   = useLocation();

  const employee              = state?.employee              ?? {};
  const preloadedAnalysis     = state?.employeeSkillAnalysis  ?? null;
  const preloadedInsights     = state?.employeeInsights       ?? null;

  const headerSubtitle = useMemo(
    () => buildEmployeeHeaderSubtitle(employee),
    [employee]
  );

  const [activeTab,            setActiveTab]            = useState(PAIC_TAB_KEYS.SKILLS_BRIDGER);
  const [chartSkills,          setChartSkills]          = useState([]);
  const [sbiScore,             setSbiScore]             = useState("--");
  const [employeeSkillAnalysis, setEmployeeSkillAnalysis] = useState(preloadedAnalysis);
  const [loading,              setLoading]              = useState(false);
  const [employeeInsights,     setEmployeeInsights]     = useState(preloadedInsights);
  const [insightsLoading,      setInsightsLoading]      = useState(false);

  // ── Data processing ────────────────────────────────────────────────────────
  // Extracts chart-ready data + SBI score + profile summary from the raw API response.

  const processAnalysisResponse = useCallback((data) => {
    const analysis = data?.personProfileAnalysis ?? {};
    const skills   = analysis.skills  ?? [];
    const tasks    = analysis.tasks   ?? [];

    setEmployeeSkillAnalysis(data);
    setChartSkills(transformSkillsForChart(skills, tasks));

    const rawSbi = data?.current_vs_required_SBI_score;
    setSbiScore(rawSbi != null ? `${Math.round(rawSbi)}%` : "--");
  }, []);

  // ── API fetch ──────────────────────────────────────────────────────────────
  // Skips the network call when the skills-map page receives pre-fetched data
  // (e.g. user navigating back from the proficiency page).

  useEffect(() => {
    if (!employee.employee_id) return;

    if (preloadedAnalysis) {
      processAnalysisResponse(preloadedAnalysis);
      return;
    }

    setLoading(true);
    getSBEmployeeJdSkillAnalysis(employee.employee_id)
      .then(processAnalysisResponse)
      .catch((err) => console.error("Employee skill analysis error:", err))
      .finally(() => setLoading(false));
  }, [employee.employee_id, preloadedAnalysis, processAnalysisResponse]);

  useEffect(() => {
    if (!employee.employee_id) return;

    if (preloadedInsights) {
      setEmployeeInsights(preloadedInsights);
      return;
    }

    setInsightsLoading(true);
    const employeeProfile = {
      name:          employee.employee_name  ?? "",
      role:          employee.role_name      ?? "",
      function:      employee.function       ?? "",
      job_grade:     String(employee.level   ?? employee.job_level ?? ""),
      business_unit: employee.business_unit  ?? "",
      location:      employee.location       ?? "",
    };
    getEmployeeSummary(employee.employee_id, employeeProfile)
      .then((data) => {
        const summary = data?.ai_generated_summary?.employee_summary ?? null;
        setEmployeeInsights(summary);
      })
      .catch((err) => console.error("Employee insights error:", err))
      .finally(() => setInsightsLoading(false));
  }, [employee.employee_id, preloadedInsights]);

  // ── Navigation ─────────────────────────────────────────────────────────────

  const handleBack = useCallback(
    () => navigate(ROUTE.SKILLS_BRIDGER),
    [navigate]
  );

  // Passes the already-fetched analysis to the proficiency page so it never
  // needs to call the API a second time.
  const handleNavigateToProficiency = useCallback(
    () =>
      navigate(ROUTE.EMPLOYEE_SKILLS_PROFICIENCY, {
        state: { employee, employeeSkillAnalysis, employeeInsights },
      }),
    [employee, employeeSkillAnalysis, employeeInsights, navigate]
  );

  // ── Dropdown items ─────────────────────────────────────────────────────────

  const rightDropdownItems = useMemo(
    () => [
      {
        key:     "skills-proficiency",
        label:   TEXT.DROPDOWN_SKILLS_PROFICIENCY,
        onClick: handleNavigateToProficiency,
      },
    ],
    [handleNavigateToProficiency]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <SkillsMapContainerStyled vertical>

      <GroupPageHeader
        activeTab={activeTab}
        onBack={handleBack}
        onTabChange={setActiveTab}
        skillsBridgerDropdownItems={SKILLS_BRIDGER_DROPDOWN_ITEMS}
        rightDropdownLabel={TEXT.DROPDOWN_SKILLS_MAP}
        rightDropdownItems={rightDropdownItems}
        pageTitle={PAGE_TEXT.PAGE_TITLE}
        pageSubtitle={headerSubtitle}
      />

      <ContentAreaStyled flex="1">

        {activeTab === PAIC_TAB_KEYS.SKILLS_BRIDGER && (
          <Spin spinning={loading}>
            <Row gutter={[24, 24]} align="top">
              <Col xs={24} lg={16}>
                <SkillsChart
                  skills={chartSkills}
                  title={PAGE_TEXT.CHART_TITLE}
                  subtitle={PAGE_TEXT.CHART_SUBTITLE}
                />
              </Col>
              <Col xs={24} lg={8}>
                <Flex vertical gap={16}>
                  <SbiCard label={PAGE_TEXT.SBI_LABEL} value={sbiScore} />
                  <EmployeeProfileCard
                    label={PAGE_TEXT.PROFILE_LABEL}
                    insights={employeeInsights}
                    insightsLoading={insightsLoading}
                  />
                </Flex>
              </Col>
            </Row>
          </Spin>
        )}

        {COMING_SOON_TABS.has(activeTab) && (
          <ComingSoonStyled align="center" justify="center">
            <Text type="secondary">{TEXT.COMING_SOON}</Text>
          </ComingSoonStyled>
        )}

      </ContentAreaStyled>

    </SkillsMapContainerStyled>
  );
};

export default EmployeeSkillsMapV2;
